import { Env } from "../settings/Env";
import { CustomMap as CustomEqMap } from "../utils/CustomEqMap";
import { XRInputHelper } from "./XRInputHelper";
export var PressType;
(function (PressType) {
    PressType[PressType["Short"] = 0] = "Short";
    PressType[PressType["Long"] = 1] = "Long";
    PressType[PressType["Hold"] = 2] = "Hold";
})(PressType || (PressType = {}));
export var PressEvent;
(function (PressEvent) {
    PressEvent[PressEvent["Press"] = 0] = "Press";
    PressEvent[PressEvent["Release"] = 1] = "Release";
    PressEvent[PressEvent["Fail"] = 2] = "Fail";
})(PressEvent || (PressEvent = {}));
function isButtonHandlerArgs(args) {
    return "button" in args;
}
function isAxesHandlerArgs(args) {
    return "axes" in args;
}
function isButtonAction(action) {
    return action.isButton;
}
function isAxesAction(action) {
    return !action.isButton;
}
const clamp = (value) => Math.max(Math.min(value, 1), -1);
const circleToSquareInFirstQuadrant = (value) => {
    let v = Object.assign({}, value);
    if (v.x === 0)
        return v; // (to avoid dividing by 0)
    if ((v.x >= 0) && (v.y >= 0)) {
        const firstOctantInQuadrant = v.x >= v.y;
        if (!firstOctantInQuadrant) {
            [v.x, v.y] = [v.y, v.x];
        }
        const resultX = Math.sqrt((v.x * v.x) + (v.y * v.y));
        const resultY = v.y * resultX / v.x;
        v = { x: resultX, y: resultY };
        if (!firstOctantInQuadrant) {
            [v.x, v.y] = [v.y, v.x];
        }
    }
    return v;
};
// @ts-ignore
const circleToSquare = (value) => {
    let v = Object.assign({}, value);
    if ((v.x >= 0) && (v.y >= 0)) {
        v = circleToSquareInFirstQuadrant(v);
    }
    else if ((v.x < 0) && (v.y >= 0)) {
        v.x = -v.x;
        v = circleToSquareInFirstQuadrant(v);
        v.x = -v.x;
    }
    else if ((v.x < 0) && (v.y < 0)) {
        v.x = -v.x;
        v.y = -v.y;
        v = circleToSquareInFirstQuadrant(v);
        v.x = -v.x;
        v.y = -v.y;
    }
    else if ((v.x >= 0) && (v.y < 0)) {
        v.y = -v.y;
        v = circleToSquareInFirstQuadrant(v);
        v.y = -v.y;
    }
    return { x: clamp(v.x), y: clamp(v.y) };
};
export class ButtonHandler {
    constructor() {
        this._SHORT_PRESS_DURATION = 200;
        this._longButtonPressTimers = new Map();
        this._shortButtonPressTimers = new Map();
        this._heldButtons = new CustomEqMap((l, r) => l.isButton === r.isButton && l.button === r.button);
        this._longAxesTimers = new CustomEqMap((l, r) => l.axes === r.axes && l.value.x === r.value.x && l.value.y === r.value.y);
        this._shortAxesTimers = new CustomEqMap((l, r) => l.axes === r.axes && l.value.x === r.value.x && l.value.y === r.value.y);
        this._actions = new CustomEqMap((l, r) => {
            if (l.isButton !== r.isButton || l.eventType !== r.eventType)
                return false;
            if (isButtonAction(l) && isButtonAction(r)) {
                return l.button === r.button;
            }
            else if (isAxesAction(l) && isAxesAction(r)) {
                return l.axes === r.axes && l.value.x === r.value.x && l.value.y === r.value.y;
            }
            return false;
        });
        this._callbacks = new Map();
    }
    _pointerId(str) {
        return XRInputHelper.getInstance()?.pointers.find(p => p.controllerId === str)?.pointerId ?? 0;
    }
    addButtonClickHandler(button, eventType, callback, mesh = null) {
        const handler = XRInputHelper.getInstance()?.addButtonStateChangedCallback(button, (args) => {
            if (mesh !== null && args.pick?.pickedMesh !== mesh)
                return;
            if (eventType === PressType.Short) {
                if (args.isPressed && args.value === 1) {
                    if (this._longButtonPressTimers.size > 0 || this._longAxesTimers.size > 0 || this._heldButtons.size > 0)
                        return;
                    let timer = this._shortButtonPressTimers.get(button);
                    if (timer)
                        return;
                    const timeout = setTimeout(() => {
                        if (this._longButtonPressTimers.size > 0 || this._longAxesTimers.size > 0 || this._heldButtons.size > 0)
                            return;
                        const handler = this._actions.get({ isButton: true, button: button, eventType: PressType.Long });
                        if (handler) {
                            const callback = this._callbacks.get(handler);
                            if (callback) {
                                let timer = this._longButtonPressTimers.get(button);
                                if (timer)
                                    return;
                                timer = {
                                    controllerId: args.controllerId, timeout: setTimeout(() => {
                                        callback(PressEvent.Release, args.pick, this._pointerId(args.controllerId));
                                    }, Env.broadcast.controller.longPressDuration)
                                };
                                this._longButtonPressTimers.set(button, timer);
                                callback(PressEvent.Press, args.pick, this._pointerId(args.controllerId));
                            }
                        }
                    }, this._SHORT_PRESS_DURATION);
                    timer = { controllerId: args.controllerId, pressTime: Date.now(), timeout };
                    this._shortButtonPressTimers.set(button, timer);
                    callback(PressEvent.Press, args.pick, this._pointerId(args.controllerId));
                }
                else if (!args.isPressed && args.value === 0) {
                    let timer = this._shortButtonPressTimers.get(button);
                    if (!timer || args.controllerId !== timer.controllerId)
                        return;
                    this._shortButtonPressTimers.delete(button);
                    clearTimeout(timer.timeout);
                    if (timer.pressTime + this._SHORT_PRESS_DURATION > Date.now())
                        callback(PressEvent.Release, args.pick, this._pointerId(args.controllerId));
                    else
                        callback(PressEvent.Fail, args.pick, this._pointerId(args.controllerId));
                }
            }
            else if (eventType === PressType.Long) {
                if (args.isPressed && args.value === 1) {
                    if (this._longButtonPressTimers.size > 0 || this._longAxesTimers.size > 0 || this._heldButtons.size > 0 || this._shortButtonPressTimers.get(button) !== undefined)
                        return;
                    let timer = this._longButtonPressTimers.get(button);
                    if (timer)
                        return;
                    timer = { controllerId: args.controllerId, timeout: setTimeout(() => {
                            callback(PressEvent.Release, args.pick, this._pointerId(args.controllerId));
                        }, Env.broadcast.controller.longPressDuration) };
                    this._longButtonPressTimers.set(button, timer);
                    callback(PressEvent.Press, args.pick, this._pointerId(args.controllerId));
                }
                else if (!args.isPressed && args.value === 0) {
                    let timer = this._longButtonPressTimers.get(button);
                    if (!timer || args.controllerId !== timer.controllerId)
                        return;
                    clearTimeout(timer.timeout);
                    this._longButtonPressTimers.delete(button);
                    callback(PressEvent.Fail, args.pick, this._pointerId(args.controllerId));
                }
            }
            else if (eventType === PressType.Hold) {
                if (this._longButtonPressTimers.size > 0 || this._longAxesTimers.size > 0)
                    return;
                if (args.isPressed && args.value === 1) {
                    if (this._heldButtons.has({ button: button, isButton: true }))
                        return;
                    this._heldButtons.set({ button: button, isButton: true }, args.controllerId);
                    callback(PressEvent.Press, args.pick, this._pointerId(args.controllerId));
                }
                else if (!args.isPressed && args.value === 0) {
                    if (!this._heldButtons.has({ button: button, isButton: true }) || this._heldButtons.get({ button: button, isButton: true }) !== args.controllerId)
                        return;
                    this._heldButtons.delete({ button: button, isButton: true });
                    callback(PressEvent.Release, args.pick, this._pointerId(args.controllerId));
                }
            }
        });
        if (handler !== undefined) {
            this._actions.set({ isButton: true, button, eventType }, handler);
            this._callbacks.set(handler, callback);
        }
    }
    _compareAxisValues(value, expectedValue, threshold, _temp) {
        // const srcValue = value;
        value = circleToSquare(value);
        // console.log(`++++ ${temp}: (${srcValue.x.toFixed(3)}, ${srcValue.y.toFixed(3)}) => (${value.x.toFixed(3)}, ${value.y.toFixed(3)})`);
        // return Math.sign(value.x) === Math.sign(expectedValue.x) && Math.abs(value.x) >= Math.abs(expectedValue.x) - threshold.x && Math.abs(value.x) <= Math.abs(expectedValue.x) + threshold.x &&
        //        Math.sign(value.y) === Math.sign(expectedValue.y) && Math.abs(value.y) >= Math.abs(expectedValue.y) - threshold.y && Math.abs(value.y) <= Math.abs(expectedValue.y) + threshold.y;
        const dx = value.x - expectedValue.x;
        const dy = value.y - expectedValue.y;
        return Math.abs(dx) <= threshold.x && Math.abs(dy) <= threshold.y;
    }
    addAxisHandler(axes, eventType, value, threshold, callback, mesh = null) {
        const handler = XRInputHelper.getInstance()?.addAxesStateChangedCallback(axes, (args) => {
            if (mesh !== null && args.pick?.pickedMesh !== mesh)
                return;
            if (eventType === PressType.Short) {
                if (this._longButtonPressTimers.size > 0 || this._longAxesTimers.size > 0 || this._heldButtons.size > 0)
                    return;
                if (this._compareAxisValues(args.value, value, threshold, "[1]")) {
                    let timer = this._shortAxesTimers.get({ axes, value });
                    if (timer)
                        return;
                    const timeout = setTimeout(() => {
                        if (this._longButtonPressTimers.size > 0 || this._longAxesTimers.size > 0 || this._heldButtons.size > 0)
                            return;
                        const handler = this._actions.get({ isButton: false, axes, value, eventType: PressType.Long });
                        if (handler) {
                            const callback = this._callbacks.get(handler);
                            if (callback) {
                                let timer = this._longAxesTimers.get({ axes, value });
                                if (timer)
                                    return;
                                timer = {
                                    controllerId: args.controllerId, timeout: setTimeout(() => {
                                        callback(PressEvent.Release, args.pick, this._pointerId(args.controllerId));
                                    }, Env.broadcast.controller.longPressDuration)
                                };
                                this._longAxesTimers.set({ axes, value }, timer);
                                callback(PressEvent.Press, args.pick, this._pointerId(args.controllerId));
                            }
                        }
                    }, this._SHORT_PRESS_DURATION);
                    timer = { controllerId: args.controllerId, pressTime: Date.now(), timeout };
                    this._shortAxesTimers.set({ axes, value }, timer);
                    callback(PressEvent.Press, args.pick, this._pointerId(args.controllerId));
                }
                else {
                    let timer = this._shortAxesTimers.get({ axes, value });
                    if (!timer || args.controllerId !== timer.controllerId)
                        return;
                    this._shortAxesTimers.delete({ axes, value });
                    if (timer.pressTime + this._SHORT_PRESS_DURATION > Date.now())
                        callback(PressEvent.Release, args.pick, this._pointerId(args.controllerId));
                    else
                        callback(PressEvent.Fail, args.pick, this._pointerId(args.controllerId));
                }
            }
            else if (eventType === PressType.Long) {
                if (this._compareAxisValues(args.value, value, threshold, "[2]")) {
                    if (this._longButtonPressTimers.size > 0 || this._longAxesTimers.size > 0 || this._heldButtons.size > 0 || this._shortAxesTimers.get({ axes, value }) !== undefined)
                        return;
                    let timer = this._longAxesTimers.get({ axes, value });
                    if (timer)
                        return;
                    timer = { controllerId: args.controllerId, timeout: setTimeout(() => {
                            callback(PressEvent.Release, args.pick, this._pointerId(args.controllerId));
                        }, Env.broadcast.controller.longPressDuration)
                    };
                    this._longAxesTimers.set({ axes, value }, timer);
                    callback(PressEvent.Press, args.pick, this._pointerId(args.controllerId));
                }
                else {
                    let timer = this._longAxesTimers.get({ axes, value });
                    if (!timer || args.controllerId !== timer.controllerId)
                        return;
                    clearTimeout(timer.timeout);
                    this._longAxesTimers.delete({ axes, value });
                    callback(PressEvent.Fail, args.pick, this._pointerId(args.controllerId));
                }
            }
            else if (eventType === PressType.Hold) {
                if (this._longButtonPressTimers.size > 0 || this._longAxesTimers.size > 0)
                    return;
                if (this._compareAxisValues(args.value, value, threshold, "[3]")) {
                    if (this._heldButtons.has({ button: axes, isButton: false }))
                        return;
                    this._heldButtons.set({ button: axes, isButton: false }, args.controllerId);
                    callback(PressEvent.Press, args.pick, this._pointerId(args.controllerId));
                }
                else {
                    if (!this._heldButtons.has({ button: axes, isButton: false }) || this._heldButtons.get({ button: axes, isButton: false }) !== args.controllerId)
                        return;
                    this._heldButtons.delete({ button: axes, isButton: false });
                    callback(PressEvent.Release, args.pick, this._pointerId(args.controllerId));
                }
            }
        });
        if (handler) {
            this._actions.set({ isButton: false, axes, value, eventType }, handler);
            this._callbacks.set(handler, callback);
        }
    }
    addHandlers(...args) {
        for (const arg of args) {
            if (isButtonHandlerArgs(arg))
                this.addButtonClickHandler(arg.button, arg.eventType, arg.callback, arg.mesh ?? null);
            else if (isAxesHandlerArgs(arg))
                this.addAxisHandler(arg.axes, arg.eventType, arg.value, Env.broadcast.controller.thumbstickThreshold, arg.callback, arg.mesh ?? null);
        }
    }
    removeButtonClickHandler(button, eventType) {
        const handler = this._actions.get({ isButton: true, button, eventType });
        if (handler) {
            XRInputHelper.getInstance()?.removeButtonStateChangedCallback(handler);
            this._actions.delete({ isButton: true, button, eventType });
            this._callbacks.delete(handler);
        }
    }
    removeAxesHandler(axes, value, eventType) {
        const handler = this._actions.get({ isButton: false, axes, value, eventType });
        if (handler) {
            XRInputHelper.getInstance()?.removeButtonStateChangedCallback(handler);
            this._actions.delete({ isButton: false, axes, value, eventType });
            this._callbacks.delete(handler);
        }
    }
    removeHandlers() {
        this._actions.forEach((v, k) => {
            if (isButtonAction(k))
                XRInputHelper.getInstance()?.removeButtonStateChangedCallback(v);
            else if (isAxesAction(k))
                XRInputHelper.getInstance()?.removeAxesStateChangedCallback(v);
        });
        this._actions.clear();
        this._longButtonPressTimers.forEach(v => clearTimeout(v.timeout));
        this._longAxesTimers.forEach(v => clearTimeout(v.timeout));
        this._longButtonPressTimers.clear();
        this._longAxesTimers.clear();
        this._shortButtonPressTimers.clear();
        this._shortAxesTimers.clear();
        this._callbacks.clear();
    }
}
